/**
 * <ClinicianReviewBadge/>
 *
 * Trust signal rendered in the hero of `/for-<discipline>` pages **only**
 * when a licensed clinician has formally signed off on that page's copy
 * (i.e. `discipline.advisor_review` is populated in
 * `frontend/src/data/disciplines.js`).
 *
 * Honesty rule: silent absence when unsigned. We never render a placeholder
 * "pending review" pill — clinicians reading the page should treat the
 * absence of the badge as "not yet advisor-reviewed" and read accordingly.
 *
 * Click opens an in-page Dialog with the full attestation (reviewer name,
 * credentials, NPI, date, items verified, expiry date). No external links
 * — keeps users on-page, gives us full control of the attestation text,
 * and avoids a broken-LinkedIn dependency.
 *
 * Schema (see /app/memory/ADVISOR_REVIEW_BRIEF_PACK.md for the source pack):
 *   advisor_review: {
 *     reviewer_name: 'Jane Doe, DNP, FNP-BC',
 *     credentials: ['DNP', 'FNP-BC'],
 *     npi: '1234567890',
 *     signed_at: '2026-02-21',        // ISO date
 *     expires_at: '2026-08-21',       // ISO date — 6mo refresh cadence
 *     items_verified: [
 *       'NP Full Practice Authority state list (per AANP, May 2026)',
 *       'NP collaborative-agreement framing for Reduced/Restricted states',
 *     ],
 *     attestation_url: null,          // optional inbound LinkedIn / verification link
 *   }
 */
import React, { useState } from 'react';
import { BadgeCheck, ShieldCheck, Calendar } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';

export function ClinicianReviewBadge({ review, disciplineName }) {
  const [open, setOpen] = useState(false);
  if (!review || !review.reviewer_name) return null;

  const signedDate = review.signed_at
    ? new Date(review.signed_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    : null;
  const expiresDate = review.expires_at
    ? new Date(review.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;
  const expired = review.expires_at ? new Date(review.expires_at) < new Date() : false;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 text-xs text-emerald-700 dark:text-emerald-300 hover:underline focus:outline-none focus:ring-2 focus:ring-emerald-400 rounded transition-colors"
        data-testid="clinician-review-badge"
      >
        <BadgeCheck className="w-3.5 h-3.5" />
        <span className="font-semibold">
          {expired ? 'Clinician-reviewed (refresh due)' : 'Clinician-reviewed'}
        </span>
        {signedDate && <span className="text-cn-text-muted">· {signedDate}</span>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="clinician-review-modal">
          <DialogHeader>
            <div className="inline-flex w-12 h-12 rounded-2xl bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 items-center justify-center mb-2">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <DialogTitle className="text-xl font-display">
              Clinician-reviewed: {disciplineName}
            </DialogTitle>
            <DialogDescription className="text-sm text-cn-text-muted">
              The regulatory, scope-of-practice, and reimbursement claims on this page
              have been independently reviewed and signed off by a licensed clinician.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-3 text-sm">
            <div className="cn-card p-4 bg-cn-bone-50 dark:bg-cn-ink-900 border-cn-border">
              <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-1">Reviewer</div>
              <div className="font-display font-bold text-cn-text dark:text-white" data-testid="clinician-review-name">
                {review.reviewer_name}
              </div>
              {review.npi && (
                <div className="text-xs text-cn-text-muted mt-1">NPI {review.npi}</div>
              )}
            </div>

            {Array.isArray(review.items_verified) && review.items_verified.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted mb-2">Items verified</div>
                <ul className="space-y-1.5">
                  {review.items_verified.map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-cn-text dark:text-cn-bone-50">
                      <BadgeCheck className="w-3.5 h-3.5 mt-1 shrink-0 text-emerald-600" />
                      <span className="leading-snug">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-cn-border">
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Signed</div>
                <div className="flex items-center gap-1.5 mt-0.5 text-cn-text dark:text-cn-bone-50">
                  <Calendar className="w-3.5 h-3.5 text-cn-text-muted" />
                  <span>{signedDate || '—'}</span>
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">Refresh due</div>
                <div className={`flex items-center gap-1.5 mt-0.5 ${expired ? 'text-amber-700 dark:text-amber-300' : 'text-cn-text dark:text-cn-bone-50'}`}>
                  <Calendar className="w-3.5 h-3.5" />
                  <span>{expiresDate || '—'}</span>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-cn-text-muted leading-snug pt-2 border-t border-cn-border">
              Regulatory and reimbursement claims drift. CaraLoom refreshes each
              clinician review every 6 months against the authoritative source
              (AANP, NASHP, CMS, AAA, WOCNCB, etc.). The page-specific source list
              is in <code className="bg-cn-bone-100 dark:bg-cn-ink-700 px-1 rounded">/app/memory/TIER2_COPY_REVIEW_FEB2026.md</code>.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default ClinicianReviewBadge;
